import { getScrollTop, scrollTo as scrollPageTo } from '@theme/scroll-container';
/** One set of real controls: responsive positioning never clones forms or IDs. */
class HeaderComposition extends HTMLElement {
  #controller;
  #resize;
  #sections;
  #frame = 0;
  #scrollFrame = 0;
  #localization;
  #localizationSection;
  #returnPoint;
  #home;
  #homeReady = false;
  // Match the icon/typography breakpoint: tablets use the mobile composition.
  #desktop = matchMedia('(min-width: 1200px)');

  connectedCallback() {
    if (this.#controller) return;
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('header:localization-ready', this.#schedule, { signal });
    this.#home = this.querySelector('.header-home-link');
    this.closest('header-component')?.addEventListener('header:visibility-settled', () => this.#updateHome(), { signal });
    this.#home?.addEventListener('click', event => {
      // Keep the real homepage URL for other pages and modified/new-tab clicks.
      if (!this.#home.hasAttribute('data-home-scroll-reveal') || event.defaultPrevented ||
          event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.#closeHamburger();
      scrollPageTo({ top: 0, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }, { signal });
    const image = this.#home?.querySelector('img');
    const revealWhenReady = async () => {
      try { await image?.decode(); } catch { /* Keep the link usable on image failure. */ }
      if (signal.aborted) return;
      requestAnimationFrame(() => {
        if (signal.aborted) return;
        this.#homeReady = true;
        this.#scheduleScroll();
      });
    };
    if (!image || image.complete) revealWhenReady();
    else {
      image.addEventListener('load', revealWhenReady, { once: true, signal });
      image.addEventListener('error', revealWhenReady, { once: true, signal });
    }
    const hamburger = this.querySelector('.header-hamburger');
    hamburger?.addEventListener('toggle', () => {
      if (!hamburger.open) {
        hamburger.querySelectorAll('details[open]').forEach(details => { details.open = false; });
      }
      this.#schedule();
    }, { signal });
    document.addEventListener('pointerdown', event => {
      if (!hamburger?.parentElement.contains(event.target)) this.#closeHamburger();
    }, { signal });
    this.addEventListener('keydown', event => {
      if (event.key !== 'Escape' || event.defaultPrevented || !hamburger?.open) return;
      event.preventDefault();
      this.#closeHamburger(true);
    }, { signal });
    this.addEventListener('focusout', event => {
      // Ignore temporary native focus loss during disclosure layout changes.
      if (!(event.relatedTarget instanceof Node)) return;
      if (!hamburger?.parentElement.contains(event.relatedTarget)) this.#closeHamburger();
    }, { signal });
    this.addEventListener('click', event => {
      if (event.target.closest('.header-hamburger a[href]')) this.#closeHamburger();
    }, { signal });
    // Popup dismissal owns history for the outer hamburger and its submenus.
    // An inner close must never make a history cleanup close the outer menu.
    this.#desktop.addEventListener('change', this.#schedule, { signal });
    window.addEventListener('resize', this.#schedule, { passive: true, signal });
    document.fonts?.addEventListener('loadingdone', this.#schedule, { signal });
    document.fonts?.ready.then(() => { if (!signal.aborted) this.#schedule(); });
    document.addEventListener('shopify:section:load', this.#schedule, { signal });
    document.addEventListener('shopify:section:unload', event => {
      if (this.#localizationSection && event.target instanceof Element && event.target.contains(this.#localizationSection)) {
        this.#restoreLocalization();
      }
    }, { signal });
    document.addEventListener('shopify:section:reorder', this.#schedule, { signal });
    window.addEventListener('pageshow', this.#schedule, { signal });
    document.addEventListener('DOMContentLoaded', this.#schedule, { once: true, signal });
    this.#resize = new ResizeObserver(this.#schedule);
    this.#resize.observe(this);
    const group = this.closest('#header-group');
    if (group) {
      this.#sections = new MutationObserver(this.#schedule);
      // Section additions matter; submenu animations, skeleton masks and cart
      // text changes do not require remeasuring/reparenting the whole header.
      this.#sections.observe(group, { childList: true });
    }
    if (this.#home?.hasAttribute('data-home-scroll-reveal')) {
      this.#home.setAttribute('data-scroll-managed', '');
      document.addEventListener('scroll', this.#scheduleScroll, { capture: true, passive: true, signal });
    }
    this.#layout();
    this.#scheduleScroll();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#homeReady = false;
    this.#resize?.disconnect();
    this.#sections?.disconnect();
    cancelAnimationFrame(this.#frame);
    cancelAnimationFrame(this.#scrollFrame);
    this.#frame = this.#scrollFrame = 0;
    this.#restoreLocalization();
    if (this.#returnPoint?.nodeType === Node.COMMENT_NODE) this.#returnPoint.remove();
    this.#returnPoint = this.#localization = this.#localizationSection = null;
    this.#home?.removeAttribute('data-scroll-managed');
    if (this.#home) this.#home.inert = false;
  }

  #schedule = () => {
    if (this.#frame || !this.#controller) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#layout();
      this.#scheduleScroll();
    });
  };

  #scheduleScroll = () => {
    if (this.#scrollFrame || !this.#controller) return;
    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = 0;
      this.#updateHome();
    });
  };

  #updateHome() {
    if (!this.#home?.hasAttribute('data-home-scroll-reveal')) {
      this.#updateMobileLocalization(false);
      return;
    }
    const wasVisible = this.#home.hasAttribute('data-visible');
    const header = this.closest('header-component');
    // Keep the downward handoff behind the header's completed slide-out.
    let pastTop = !!header?.hasAttribute('data-header-passed');
    const cover = document.querySelector('product-overlay-hero');
    const coverBottom = cover?.getBoundingClientRect().bottom;
    const headerHeight = header?.offsetHeight || 0;
    if (header?.getAttribute('sticky') !== 'scroll-up') {
      pastTop = header?.getAttribute('sticky') === 'always'
        ? !!cover && coverBottom <= headerHeight
        : !!header && header.getBoundingClientRect().bottom <= 0;
    }
    // Fade over scroll distance, not a delayed timer: even a fast upward fling
    // must finish hiding the small logo before the cover clears the menu.
    // Using the whole cover also handles a flat fallback with a baked-in logo.
    const clearance = cover ? headerHeight - coverBottom : 96;
    const opacity = Math.max(0, Math.min(1, clearance / 96));
    const previewLogo = !!window.Shopify?.designMode && !(this.hasAttribute('data-mobile-localization-active') && !this.#desktop.matches);
    const canReveal = header?.getAttribute('sticky') !== 'scroll-up' || wasVisible || header.dataset.stickyState === 'idle';
    const visible = this.#homeReady && ((pastTop && canReveal && opacity > 0) || previewLogo);
    const logoOpacity = String(previewLogo ? 1 : opacity);
    if (this.#home.style.getPropertyValue('--header-home-scroll-opacity') !== logoOpacity) {
      this.#home.style.setProperty('--header-home-scroll-opacity', logoOpacity);
    }
    if (visible !== wasVisible) this.#home.toggleAttribute('data-visible', visible);
    if (this.#home.inert !== !visible) this.#home.inert = !visible;
    this.#updateMobileLocalization(visible);
  }

  #updateMobileLocalization(logoVisible) {
    const slot = this.querySelector('[data-header-mobile-localization-slot]');
    if (!slot) return;
    const nearTop = !this.#home?.hasAttribute('data-home-scroll-reveal') || getScrollTop() <= 24;
    const hidden = !this.hasAttribute('data-mobile-localization-active') || logoVisible || !nearTop;
    if (this.hasAttribute('data-mobile-localization-hidden') !== hidden) {
      this.toggleAttribute('data-mobile-localization-hidden', hidden);
      if (hidden) slot.querySelectorAll('details[open]').forEach(details => { details.open = false; });
    }
    if (slot.inert !== hidden) slot.inert = hidden;
  }

  #closeHamburger(restoreFocus = false) {
    const menu = this.querySelector('.header-hamburger');
    if (!menu?.open) return;
    menu.querySelectorAll('details[open]').forEach(details => { details.open = false; });
    menu.open = false;
    if (restoreFocus) menu.querySelector('summary')?.focus({ preventScroll: true });
  }

  #restoreLocalization() {
    if (this.#localization && this.#returnPoint?.isConnected && this.contains(this.#localization)) {
      this.#returnPoint.after(this.#localization);
    } else if (this.#localization && this.contains(this.#localization)) {
      // Its source section was replaced/removed while the controls were mounted.
      this.#localization.remove();
    }
    this.#localization?.removeAttribute('data-header-embedded');
    this.removeAttribute('data-mobile-localization-active');
  }

  #layout() {
    if (!this.isConnected) return;
    const start = this.querySelector('.header-inline-bar__start');
    const middle = this.querySelector('.header-inline-bar__mid');
    const end = this.querySelector('.header-inline-bar__end');
    const slot = this.querySelector('[data-header-localization-slot]');
    if (!start || !middle || !end || !slot) return;
    const side = (this.#desktop.matches ? this.dataset.homeDesktop : this.dataset.homeMobile) || 'left';
    const homeParent = side === 'center' ? middle : side === 'right' ? end : start;
    if (this.#home && this.#home.parentElement !== homeParent) homeParent.prepend(this.#home);
    this.dataset.homeSide = side;
    const mode = (this.#desktop.matches ? this.dataset.navigationDesktop : this.dataset.navigationMobile) || 'inline';
    const shell = this.querySelector('.header-menu-shell');
    const hamburger = shell?.querySelector('.header-hamburger');
    const navigation = shell?.querySelector('header-inline-menu');
    const panel = hamburger?.querySelector('.header-hamburger__panel');
    const compact = mode !== 'inline';
    if (shell && hamburger && navigation && panel) {
      const parent = mode === 'hamburger-left' ? start : middle;
      const navParent = compact ? panel : shell;
      if (mode === 'hamburger-left') {
        if (shell.parentElement !== parent || parent.firstElementChild !== shell) parent.prepend(shell);
      } else if (shell.parentElement !== parent) parent.append(shell);
      if (navigation.parentElement !== navParent) {
        this.#closeHamburger();
        navParent.prepend(navigation);
      }
      this.toggleAttribute('data-hamburger', compact);
    }
    const search = this.querySelector('[data-header-menu-search]');
    if (search) {
      const besideHamburger = !this.#desktop.matches && compact && shell;
      if (besideHamburger) {
        if (search.parentElement !== shell) shell.append(search);
      } else if (this.#home?.parentElement === start) {
        if (this.#home.nextElementSibling !== search) this.#home.after(search);
      } else if (search.parentElement !== start || start.firstElementChild !== search) {
        start.prepend(search);
      }
      this.toggleAttribute('data-search-beside-hamburger', !!besideHamburger);
    } else {
      this.removeAttribute('data-search-beside-hamburger');
    }
    const alignment = this.#desktop.matches ? this.dataset.menuDesktop : this.dataset.menuMobile;
    const screenCenter = (!!this.#home && side === 'center') || mode === 'hamburger-center' || (!compact && alignment === 'screen');
    this.toggleAttribute('data-screen-center', screenCenter);

    if (!this.#localization?.isConnected || !this.#localizationSection?.isConnected ||
        !this.#localizationSection.contains(this.#returnPoint)) {
      this.#restoreLocalization();
      if (this.#localization) this.#resize.unobserve(this.#localization);
      if (this.#returnPoint?.nodeType === Node.COMMENT_NODE) this.#returnPoint.remove();
      // Shopify's section wrapper must remain a sibling of the header section.
      // Only move its plain content wrapper, never editor metadata or assets.
      this.#localizationSection = [...(this.closest('#header-group')?.querySelectorAll('.shopify-section.localization-bar-section') || [])]
        .find(section => section.querySelector('[data-header-localization-anchor], [data-header-localization-content]'));
      const anchor = this.#localizationSection?.querySelector('[data-header-localization-anchor]');
      this.#localization = anchor
        ? document.getElementById(anchor.dataset.headerLocalizationAnchor)
        : this.#localizationSection?.querySelector('[data-header-localization-content]');
      this.#returnPoint = anchor || null;
      if (this.#localization) {
        if (!this.#returnPoint) {
          this.#returnPoint = document.createComment('Header localization return position');
          this.#localization.before(this.#returnPoint);
        }
        this.#resize.observe(this.#localization);
      }
    }

    const localization = this.#localization;
    const sourceVisible = localization && getComputedStyle(this.#localizationSection).display !== 'none';
    const inMobileMenu = !this.#desktop.matches && compact && sourceVisible &&
      localization.dataset.mobilePlacement === 'hamburger';
    if (sourceVisible && this.#desktop.matches && this.hasAttribute('data-localization-inside')) {
      // One stable desktop slot. Wrap navigation instead of undocking the bar
      // whenever a font/resize measurement changes near the fit threshold.
      if (localization.getAttribute('data-header-embedded') !== '') localization.setAttribute('data-header-embedded', '');
      if (localization.parentElement !== slot) slot.append(localization);
      this.removeAttribute('data-mobile-localization-active');
    } else if (inMobileMenu && panel) {
      if (localization.getAttribute('data-header-embedded') !== 'menu') localization.setAttribute('data-header-embedded', 'menu');
      if (localization.parentElement !== panel) panel.append(localization);
      this.removeAttribute('data-mobile-localization-active');
    } else if (!sourceVisible || this.#desktop.matches || localization?.getAttribute('data-header-embedded') !== 'mobile') {
      this.#restoreLocalization();
    }

    // Sum actual menu items rather than the stretched navigation container.
    const menu = this.querySelector('.header-inline-nav__list');
    const menuWidth = compact ? (mode === 'hamburger-center' ? shell?.getBoundingClientRect().width || 44 : 0) : [...(menu?.children || [])].reduce((sum, item) => {
      const style = getComputedStyle(item);
      return sum + item.getBoundingClientRect().width + (parseFloat(style.marginInlineStart) || 0) + (parseFloat(style.marginInlineEnd) || 0);
    }, 0);
    const measuredGap = parseFloat(getComputedStyle(this).columnGap);
    const gap = Number.isFinite(measuredGap) ? measuredGap : 12;
    const homeWidth = this.#home?.getBoundingClientRect().width || 0;
    const middleWidth = Math.max(menuWidth, side === 'center' ? homeWidth : 0);
    const centerLocalization = !this.#desktop.matches && sourceVisible && localization.dataset.mobilePlacement === 'center';
    const sideWidth = (column, exclude = null) => [...column.children].filter(child => child !== exclude && child.getClientRects().length &&
      !(centerLocalization && child === this.#home)).reduce((sum, child, i) => {
      const style = getComputedStyle(child);
      return sum + child.getBoundingClientRect().width + (parseFloat(style.marginInlineStart) || 0) + (parseFloat(style.marginInlineEnd) || 0) + (i ? gap : 0);
    }, (parseFloat(getComputedStyle(column).paddingInlineStart) || 0) + (parseFloat(getComputedStyle(column).paddingInlineEnd) || 0));
    const leftWidth = sideWidth(start);
    if (this.#desktop.matches && sourceVisible && localization.parentElement === slot) {
      const fixedRight = sideWidth(end, slot);
      const available = screenCenter
        ? (this.clientWidth - middleWidth - gap * 2 - 24) / 2 - fixedRight - gap
        : this.clientWidth - leftWidth - fixedRight - middleWidth - gap * 3 - 24;
      const width = `${Math.max(84, available)}px`;
      if (slot.style.getPropertyValue('--header-localization-max-width') !== width) {
        slot.style.setProperty('--header-localization-max-width', width);
      }
    }
    const rightWidth = sideWidth(end);
    const baseWidth = (screenCenter ? 2 * Math.max(leftWidth, rightWidth) : leftWidth + rightWidth) + middleWidth + gap * 2 + 24;
    this.toggleAttribute('data-menu-wrap', baseWidth > this.clientWidth);
    if (compact && hamburger?.open && shell) {
      const rect = shell.getBoundingClientRect();
      const width = Math.min(340, window.innerWidth - 24);
      const left = Math.max(12, Math.min(rect.left + (mode === 'hamburger-center' ? (rect.width - width) / 2 : 0), window.innerWidth - width - 12));
      shell.style.setProperty('--header-panel-left', `${left - rect.left}px`);
      shell.style.setProperty('--header-panel-height', `${Math.max(44, window.innerHeight - rect.bottom - 20)}px`);
    }

    if (!localization || !sourceVisible || this.#desktop.matches || inMobileMenu) return;
    const mobileSlot = this.querySelector('[data-header-mobile-localization-slot]');
    if (centerLocalization && mobileSlot) {
      // Fit the controls in place, never undock them into an extra row.
      // The retired localization_center_mobile flag deliberately no longer
      // restores the below-menu placement from older saved configurations.
      const available = Math.max(0, this.clientWidth - 2 * Math.max(leftWidth, rightWidth) - gap * 2 - 8);
      const width = `${Math.min(188, available)}px`;
      if (mobileSlot.style.getPropertyValue('--header-mobile-localization-width') !== width) {
        mobileSlot.style.setProperty('--header-mobile-localization-width', width);
      }
      if (localization.getAttribute('data-header-embedded') !== 'mobile') localization.setAttribute('data-header-embedded', 'mobile');
      if (localization.parentElement !== mobileSlot) mobileSlot.append(localization);
      this.setAttribute('data-mobile-localization-active', '');
      this.#updateMobileLocalization(!!this.#home?.hasAttribute('data-visible'));
      return;
    }
    this.removeAttribute('data-mobile-localization-active');
    this.#restoreLocalization();
  }
}

if (!customElements.get('header-composition')) customElements.define('header-composition', HeaderComposition);
